import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Users, Briefcase, TrendingUp, MessageCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import RelatedResources from "../components/RelatedResources";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20ITR%20Filing%20in%20Coimbatore";

const FAQS = [
  { q: "What is the last date to file ITR for FY 2024-25?", a: "For salaried individuals and non-audit cases, the due date is 31st July 2025. For taxpayers requiring audit (businesses with turnover above ₹1 crore, professionals above ₹50 lakh), it is 31st October 2025. Belated returns can be filed till 31st December 2025 with a ₹5,000 penalty (₹1,000 if income < ₹5L)." },
  { q: "Should I choose the old or new tax regime?", a: "The new regime is now the default and offers lower slab rates plus a ₹60,000 rebate under Section 87A (FY 2025-26 onwards) for income up to ₹12 lakh. The old regime makes sense only if your deductions (80C, 80D, HRA, home loan interest, etc.) exceed approximately ₹3.5–4 lakh. Use our Income Tax Calculator to compare side by side." },
  { q: "Which ITR form should I file?", a: "ITR-1 (Sahaj) for salaried with income up to ₹50 lakh and one house property. ITR-2 if you have capital gains, multiple properties, or foreign income. ITR-3 for business income or professional services. ITR-4 (Sugam) for presumptive income (44AD/44ADA)." },
  { q: "How can I maximize my refund?", a: "Claim every eligible deduction: 80C investments (up to ₹1.5L), 80D health insurance, HRA exemption, home loan interest under Section 24, education loan interest under 80E, and 80G donations. We review your Form 16, Form 26AS, and AIS to ensure nothing is missed." },
  { q: "What documents do I need to file my ITR?", a: "Form 16 from employer, Form 26AS, Annual Information Statement (AIS), bank interest certificates, capital gains statements, rent receipts (for HRA), 80C investment proofs, home loan interest certificate, health insurance premium receipts, and any other income/deduction proofs." },
  { q: "What if I miss the ITR deadline?", a: "Belated returns attract a late filing fee of up to ₹5,000 (₹1,000 if total income is below ₹5L), interest at 1% per month on unpaid tax under Section 234A, and you lose the right to carry forward losses (other than house property loss)." },
  { q: "How long does an ITR refund take?", a: "Refunds are typically credited within 2–6 weeks of e-verification. Pre-validate your bank account and link PAN with Aadhaar to avoid delays. If your refund is delayed beyond 6 months, you can raise a grievance on the e-filing portal." },
];

const RELATED = [
  { to: "/income-tax-calculator", label: "Income Tax Calculator", description: "Old vs New regime comparison", tag: "Free Tool" },
  { to: "/itr-checklist", label: "ITR Filing Checklist", description: "Free downloadable checklist for salaried", tag: "Template" },
  { to: "/tds-filing", label: "TDS Filing", description: "TDS compliance for businesses", tag: "Service" },
  { to: "/health-insurance-advisory", label: "Health Insurance Advisory", description: "Save tax under Section 80D", tag: "Service" },
  { to: "/tds-penalty-calculator", label: "TDS Penalty Calculator", description: "Calculate Section 234E and 201(1A) interest", tag: "Free Tool" },
  { to: "/compliance-roadmap", label: "Business Compliance Roadmap", description: "Find what your business actually needs", tag: "Tool" },
];

const ITRFilingPage = () => {
  useCanonical("/itr-filing");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Income Tax Return Filing in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Expert ITR filing services for salaried individuals, professionals, and businesses in Coimbatore."
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>ITR Filing in Coimbatore | Expert Income Tax Returns | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Expert ITR filing services in Coimbatore for salaried individuals, professionals, and businesses. Maximize refunds, ensure accuracy. Contact WINTRUST SOLUTIONS!" />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4">
              <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">ITR Filing</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              Income Tax Return (ITR) Filing in Coimbatore
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Filing your Income Tax Return (ITR) accurately and on time is a crucial financial responsibility. At WINTRUST SOLUTIONS, we offer expert ITR filing services for salaried individuals, professionals, and business owners in Coimbatore, ensuring you maximize your tax savings and remain fully compliant.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary">File Your ITR Now</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            ITR Filing for Salaried Individuals, Professionals & Businesses
          </h2>
          <p className="text-slate-600 mb-8">We cater to a diverse range of clients, including:</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <Users size={32} />, title: "Salaried Employees", desc: "We help you claim deductions for HRA, 80C, 80D, and more. Maximize your refunds with expert filing." },
              { icon: <Briefcase size={32} />, title: "Business Owners", desc: "We handle complex ITR-3 and ITR-4 filings, including profit and loss statements and balance sheets." },
              { icon: <TrendingUp size={32} />, title: "Freelancers & Professionals", desc: "We ensure all your professional income and expenses are correctly reported. Stay compliant and save tax." },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-lg text-center">
                <div className="text-gold mb-4 flex justify-center">{item.icon}</div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Savings */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">
                Maximize Your Tax Refunds with Expert Filing
              </h2>
              <p className="text-slate-600 mb-6">
                Our team meticulously reviews your financial data to identify every possible deduction and exemption, ensuring you receive the maximum refund you are legally entitled to.
              </p>
              <ul className="space-y-3">
                {[
                  "Section 80C - Investments up to ₹1.5 Lakh",
                  "Section 80D - Health Insurance Premiums",
                  "HRA Exemption for Salaried Individuals",
                  "Section 80E - Education Loan Interest",
                  "Section 24 - Home Loan Interest up to ₹2 Lakh",
                  "Section 80G - Charitable Donations",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-gold flex-shrink-0" size={20} />
                    <span className="text-navy">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-navy mb-4">ITR Forms We Handle</h3>
              <div className="space-y-4">
                {[
                  { form: "ITR-1 (Sahaj)", desc: "For salaried individuals with income up to ₹50 Lakh" },
                  { form: "ITR-2", desc: "For individuals with capital gains or foreign income" },
                  { form: "ITR-3", desc: "For business owners and professionals" },
                  { form: "ITR-4 (Sugam)", desc: "For presumptive income scheme (44AD/44ADA)" },
                ].map((item, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-semibold text-navy">{item.form}</p>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4 font-heading">
            File Your ITR with Confidence
          </h2>
          <p className="text-navy/80 mb-6 max-w-2xl mx-auto">
            Don't miss the deadline. File your Income Tax Return with WINTRUST SOLUTIONS and get maximum refunds. Filed with complete accuracy based on documents provided.
          </p>
          <a href="#contact" className="bg-navy text-white px-8 py-3 rounded-md font-semibold hover:bg-navy-900 transition-colors">
            File Your ITR Now
          </a>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={FAQS} testId="itr-fil-faq" />

      {/* Related Resources */}
      <RelatedResources
        title="Related Tools & Services"
        subtitle="Make ITR filing easier with these free resources"
        items={RELATED}
        testId="itr-fil-related"
      />

      <ContactForm />
    </>
  );
};

export default ITRFilingPage;
